package horcrux.frontend.common.views.collections

import horcrux.common.HasBetterToString
import horcrux.common.api.dtos.ChatPageID
import horcrux.common.api.dtos.ChatPageMetadata
import horcrux.common.api.dtos.types.CollectionID
import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.HasID

open class MetadataByID<ID: HasBetterToString, Metadata : HasID<ID>, MetadataByIDImpl>(
    private val metadataList: List<Metadata>,
    private val implListConstructor: (List<Metadata>) -> MetadataByIDImpl,
    private val implMapConstructor: (Map<ID, Metadata>) -> MetadataByIDImpl,
) :
    Map<ID, Metadata> {
    protected val metadataByID = metadataList.associateBy { it.id }
    override val entries: Set<Map.Entry<ID, Metadata>> = metadataByID.entries
    override val keys: Set<ID> = metadataByID.keys
    override val size: Int = metadataByID.size
    override val values: Collection<Metadata> = metadataByID.values
    override fun isEmpty(): Boolean = metadataByID.isEmpty()
    override fun get(key: ID): Metadata? = metadataByID[key]
    override fun containsValue(value: Metadata): Boolean = metadataByID.containsValue(value)
    override fun containsKey(key: ID): Boolean = metadataByID.containsKey(key)
    fun add(metadata: Metadata): MetadataByIDImpl =
        implListConstructor(metadataList + metadata)

    fun replace(metadata: Metadata): MetadataByIDImpl =
        implMapConstructor(metadataByID.filterKeys { it != metadata.id } + (metadata.id to metadata))

    fun remove(metadata: Metadata): MetadataByIDImpl =
        implMapConstructor(metadataByID.filterNot { it.key == metadata.id })
}

class CollectionMetadataByID(collectionMetadataList: List<CollectionMetadata> = listOf()) :
    MetadataByID<CollectionID, CollectionMetadata, CollectionMetadataByID>(
        collectionMetadataList,
        { CollectionMetadataByID(it) },
        { CollectionMetadataByID(it) }
    ) {
    private constructor(metadataByID: Map<CollectionID, CollectionMetadata>) : this(metadataByID.values.toList())

    val collectionNames = metadataByID.values.map { it.collectionName }.toSet()
}

class ChatPageMetadataByID(chatPageMetadataList: List<ChatPageMetadata> = listOf()) :
    MetadataByID<ChatPageID, ChatPageMetadata, ChatPageMetadataByID>(
        chatPageMetadataList,
        { ChatPageMetadataByID(it) },
        { ChatPageMetadataByID(it) },
    ) {
    private constructor(metadataByID: Map<ChatPageID, ChatPageMetadata>) : this(metadataByID.values.toList())
}
